import React, { useState } from 'react'
import cn from 'classnames'
import { Clock } from 'lucide-react'
import api from 'api/api'
import { refreshDirectMessageRequestFeeds } from 'lib/directMessageRequestFeeds'
import { cacheQuest } from 'state/cache'
import toast from 'utils/toast'
import useGetUser from 'refactor/hooks/api/useGetUser'
import { directMessageCounterpartId, type DirectMessageRequestSide } from 'lib/directMessages'
import type { Quest } from 'types/quests'
import styles from './direct-message-request-bar.module.scss'

type RequestQuest = Pick<Quest, 'id' | 'dm_user_ids' | 'dm_request_status'> & Partial<Quest>

// The other person's handle, fetched if nobody has cached them yet.
function useCounterpartName(quest: RequestQuest) {
    const counterpartId = directMessageCounterpartId(quest, gon.currentUser?.id)
    const { user } = useGetUser(counterpartId || undefined, {
        enabled: !!counterpartId,
        refetchOnMount: false,
    })
    const name = user?.username || user?.name
    return name ? `@${name}` : null
}

// The composer's place in a conversation waiting on a message request. The
// recipient accepts (the conversation opens) or declines (it leaves their
// Requests; they can still accept later, and the sender isn't told). The
// sender, with their one message sent, waits.
export default function DirectMessageRequestBar({
    quest,
    side,
}: {
    quest: RequestQuest
    side: DirectMessageRequestSide
}) {
    const handle = useCounterpartName(quest)
    const [busy, setBusy] = useState<'accept' | 'decline' | null>(null)

    const answer = async (choice: 'accept' | 'decline') => {
        if (busy) return
        setBusy(choice)
        try {
            const result = await api.answerMessageRequest(String(quest.id), choice)
            if (!result) return
            if (result.ok === false) {
                toast.error(result.error)
                return
            }
            cacheQuest(result.quest)
            void refreshDirectMessageRequestFeeds()
            window.dispatchEvent(
                new CustomEvent('quest_updated', { detail: { questId: result.quest.id } }),
            )
        } finally {
            setBusy(null)
        }
    }

    if (side === 'outgoing') {
        const waitingOn = handle ? `${handle} accepts` : 'they accept'
        return (
            <div className={styles.bar} data-testid="dm-request-sent">
                <Clock className={styles.icon} aria-hidden />
                <span className={styles.text}>
                    Message request sent. You can send more once {waitingOn}.
                </span>
            </div>
        )
    }

    const declined = quest.dm_request_status === 'declined'
    return (
        <div className={styles.bar} data-testid="dm-request-incoming">
            <span className={styles.text}>
                {declined ? (
                    'You declined this message request.'
                ) : (
                    <>
                        <span className={styles.name}>{handle || 'Someone'}</span> wants to message
                        you.
                    </>
                )}
            </span>
            <div className={styles.actions}>
                {!declined && (
                    <button
                        type="button"
                        className={styles.button}
                        onClick={() => answer('decline')}
                        disabled={!!busy}
                        title="They won't be told"
                        data-testid="dm-request-decline"
                    >
                        Decline
                    </button>
                )}
                <button
                    type="button"
                    className={cn(styles.button, styles.accept)}
                    onClick={() => answer('accept')}
                    disabled={!!busy}
                    data-testid="dm-request-accept"
                >
                    Accept
                </button>
            </div>
        </div>
    )
}

// Above the sender's composer before their one message.
export function DirectMessageRequestHint({ quest }: { quest: RequestQuest }) {
    const handle = useCounterpartName(quest)
    const who = handle ? `${handle} doesn't` : "They don't"
    return (
        <div className={styles.hint} data-testid="dm-request-hint">
            {who} follow you, so this starts a message request: one message until they accept.
        </div>
    )
}
